import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useWallets } from '../WalletContext';
import AIAnalysis from './AIAnalysis';
import BudgetTracker from './BudgetTracker';
import YearlyBudgetTracker from './YearlyBudgetTracker';
import Recurring from './Recurring';

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import './Analytics.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

function Analytics() {
  const { selectedWallets, getSelectedWalletsDisplay, getSelectedCurrency, getCurrencySymbol } = useWallets();
  
  const [income, setIncome] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [budgetRefreshKey, setBudgetRefreshKey] = useState(0);
  const [categoryMapping, setCategoryMapping] = useState({});
  
  // Filter state
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  
  // Debug: log when selectedWallets changes
  console.log('Analytics render - selectedWallets:', selectedWallets);

  useEffect(() => {
    fetchData();
  }, []); // Only fetch data on mount, not when selectedWallets changes
  
  // The filtering happens reactively in getFilteredIncome/getFilteredExpenses
  // which are called during render, so no need to refetch when wallets change

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch personal income, expenses, households, and category mapping
      const [incomeRes, expensesRes, householdsRes, categoriesRes] = await Promise.all([
        api.get('/personal/income?limit=1000'),
        api.get('/personal/expenses?limit=1000'),
        api.get('/household'),
        api.get('/settings/categories')
      ]);
      
      // Build category to budget_group mapping
      const mapping = {};
      const categories = categoriesRes.data?.categories || categoriesRes.data || {};
      if (categories) {
        Object.entries(categories).forEach(([catName, catData]) => {
          mapping[catName.toLowerCase()] = catData.budget_group;
          if (catData.subcategories) {
            catData.subcategories.forEach(subcat => {
              mapping[subcat.toLowerCase()] = catData.budget_group;
            });
          }
        });
      }
      setCategoryMapping(mapping);
      
      setIncome(incomeRes.data);
      
      // Fetch household expenses with splits for each household
      let allExpenses = [...expensesRes.data];
      
      for (const household of householdsRes.data) {
        try {
          const householdExpensesRes = await api.get(`/household/${household.id}/expenses-with-splits`);
          // Convert household expenses to a format similar to personal expenses
          // Use the user's split amount instead of full amount
          const currentUserRes = await api.get('/auth/me');
          const currentUserId = currentUserRes.data.id;
          
          const householdExpenses = householdExpensesRes.data.map(exp => {
            const userSplit = exp.splits?.find(s => s.user_id === currentUserId);
            return {
              ...exp,
              amount: userSplit ? userSplit.split_amount : exp.amount,
              expense_type: 'household',
              original_amount: exp.amount,
              wallet_id: household.wallet_id // Associate with household's wallet
            };
          });
          
          allExpenses = [...allExpenses, ...householdExpenses];
        } catch (err) {
          console.error(`Error fetching household ${household.id} expenses:`, err);
        }
      }
      
      setExpenses(allExpenses);
      
      // Set default year to most recent year with data
      const allDates = [
        ...incomeRes.data.map(i => new Date(i.date).getFullYear()),
        ...allExpenses.map(e => new Date(e.date).getFullYear())
      ];
      const uniqueYears = [...new Set(allDates)].sort((a, b) => b - a);
      if (uniqueYears.length > 0) {
        setSelectedYear(uniqueYears[0]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredIncome = () => {
    let filtered = income;
    
    // Filter by selected wallets (selectedWallets is always an array of wallet IDs)
    if (selectedWallets.length > 0) {
      filtered = filtered.filter(item => selectedWallets.includes(item.wallet_id));
    }
    
    if (selectedMonth !== 'all') {
      filtered = filtered.filter(item => {
        const date = new Date(item.date);
        return date.getMonth() === parseInt(selectedMonth) && date.getFullYear() === selectedYear;
      });
    } else {
      filtered = filtered.filter(item => {
        const date = new Date(item.date);
        return date.getFullYear() === selectedYear;
      });
    }
    
    return filtered;
  };

  const getFilteredExpenses = () => {
    let filtered = expenses;
    
    // Filter by selected wallets - applies to ALL expenses including household
    if (selectedWallets.length > 0) {
      filtered = filtered.filter(item => selectedWallets.includes(item.wallet_id));
    }
    
    if (selectedMonth !== 'all') {
      filtered = filtered.filter(item => {
        const date = new Date(item.date);
        return date.getMonth() === parseInt(selectedMonth) && date.getFullYear() === selectedYear;
      });
    } else {
      filtered = filtered.filter(item => {
        const date = new Date(item.date);
        return date.getFullYear() === selectedYear;
      });
    }
    
    return filtered;
  };

  const getTotals = () => {
    const filteredIncome = getFilteredIncome();
    const filteredExpenses = getFilteredExpenses();
    
    const totalIncome = filteredIncome.reduce((sum, item) => sum + parseFloat(item.net_amount || 0), 0);
    const totalExpenses = filteredExpenses.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
    
    // Savings = expenses where budget_group is "savings"
    const savingsAmount = filteredExpenses
      .filter(item => {
        const category = item.category?.toLowerCase();
        const budgetGroup = categoryMapping[category];
        return budgetGroup === 'savings';
      })
      .reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
    
    const remaining = totalIncome - totalExpenses;
    
    return {
      income: totalIncome,
      expenses: totalExpenses,
      savings: savingsAmount,
      remaining: remaining
    };
  };

  const getMonthlyData = () => {
    // If viewing a specific month, show daily data
    if (selectedMonth !== 'all') {
      const monthIndex = parseInt(selectedMonth);
      const daysInMonth = new Date(selectedYear, monthIndex + 1, 0).getDate();
      const dayLabels = Array.from({ length: daysInMonth }, (_, i) => (i + 1).toString());
      const dailyIncome = new Array(daysInMonth).fill(0);
      const dailyExpenses = new Array(daysInMonth).fill(0);
      
      const filteredIncome = income.filter(item => {
        const date = new Date(item.date);
        if (selectedWallets.length > 0 && !selectedWallets.includes(item.wallet_id)) {
          return false;
        }
        return date.getMonth() === monthIndex && date.getFullYear() === selectedYear;
      });
      
      const filteredExpenses = expenses.filter(item => {
        const date = new Date(item.date);
        // Filter ALL expenses by wallet (including household)
        if (selectedWallets.length > 0 && !selectedWallets.includes(item.wallet_id)) {
          return false;
        }
        return date.getMonth() === monthIndex && date.getFullYear() === selectedYear;
      });
      
      filteredIncome.forEach(item => {
        const day = new Date(item.date).getDate() - 1; // 0-indexed
        dailyIncome[day] += parseFloat(item.net_amount || 0);
      });
      
      filteredExpenses.forEach(item => {
        const day = new Date(item.date).getDate() - 1; // 0-indexed
        dailyExpenses[day] += parseFloat(item.amount || 0);
      });
      
      return {
        labels: dayLabels,
        income: dailyIncome,
        expenses: dailyExpenses,
        isDaily: true
      };
    }
    
    // If viewing all months, show monthly data
    const filteredIncome = income.filter(item => {
      const date = new Date(item.date);
      if (selectedWallets.length > 0 && !selectedWallets.includes(item.wallet_id)) {
        return false;
      }
      return date.getFullYear() === selectedYear;
    });
    
    const filteredExpenses = expenses.filter(item => {
      const date = new Date(item.date);
      // Filter ALL expenses by wallet (including household)
      if (selectedWallets.length > 0 && !selectedWallets.includes(item.wallet_id)) {
        return false;
      }
      return date.getFullYear() === selectedYear;
    });
    
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthlyIncome = new Array(12).fill(0);
    const monthlyExpenses = new Array(12).fill(0);
    
    filteredIncome.forEach(item => {
      const month = new Date(item.date).getMonth();
      monthlyIncome[month] += parseFloat(item.net_amount || 0);
    });
    
    filteredExpenses.forEach(item => {
      const month = new Date(item.date).getMonth();
      monthlyExpenses[month] += parseFloat(item.amount || 0);
    });
    
    return {
      labels: monthNames,
      income: monthlyIncome,
      expenses: monthlyExpenses,
      isDaily: false
    };
  };

  const getCategoryBreakdown = () => {
    const filteredExpenses = getFilteredExpenses();
    const categoryTotals = {};
    
    filteredExpenses.forEach(expense => {
      const category = expense.category;
      // Exclude savings from category breakdown (it's shown separately)
      if (category?.toLowerCase() === 'savings' || category?.toLowerCase() === 'saving') {
        return;
      }
      if (!categoryTotals[category]) {
        categoryTotals[category] = 0;
      }
      categoryTotals[category] += parseFloat(expense.amount || 0);
    });
    
    const sortedCategories = Object.entries(categoryTotals)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6);
    
    return {
      labels: sortedCategories.map(([cat]) => cat),
      data: sortedCategories.map(([, amount]) => amount)
    };
  };

  const getAvailableYears = () => {
    const allDates = [
      ...income.map(i => new Date(i.date).getFullYear()),
      ...expenses.map(e => new Date(e.date).getFullYear())
    ];
    const uniqueYears = [...new Set(allDates)].sort((a, b) => b - a);
    return uniqueYears.length > 0 ? uniqueYears : [new Date().getFullYear()];
  };

  const months = [
    { value: 'all', label: 'All Months' },
    { value: '0', label: 'January' },
    { value: '1', label: 'February' },
    { value: '2', label: 'March' },
    { value: '3', label: 'April' },
    { value: '4', label: 'May' },
    { value: '5', label: 'June' },
    { value: '6', label: 'July' },
    { value: '7', label: 'August' },
    { value: '8', label: 'September' },
    { value: '9', label: 'October' },
    { value: '10', label: 'November' },
    { value: '11', label: 'December' }
  ];

  const goToPreviousMonth = () => {
    if (selectedMonth === 'all') {
      // Go to December of the selected year
      setSelectedMonth('11');
      // Don't change the year, stay in the selected year
    } else {
      const currentMonth = parseInt(selectedMonth);
      if (currentMonth === 0) {
        setSelectedMonth('11');
        setSelectedYear(selectedYear - 1);
      } else {
        setSelectedMonth((currentMonth - 1).toString());
      }
    }
  };

  const goToNextMonth = () => {
    if (selectedMonth === 'all') {
      // Go to January of the selected year
      setSelectedMonth('0');
      // Don't change the year, stay in the selected year
    } else {
      const currentMonth = parseInt(selectedMonth);
      if (currentMonth === 11) {
        setSelectedMonth('0');
        setSelectedYear(selectedYear + 1);
      } else {
        setSelectedMonth((currentMonth + 1).toString());
      }
    }
  };

  const getPeriodLabel = () => {
    if (selectedMonth === 'all') {
      return `${selectedYear}`;
    }
    const monthName = months.find(m => m.value === selectedMonth)?.label || '';
    return `${monthName} ${selectedYear}`;
  };

  const formatMoney = (amount) => {
    const currency = getSelectedCurrency();
    const symbol = getCurrencySymbol(currency);
    return `${symbol}${Math.abs(amount).toFixed(2)}`;
  };

  const totals = getTotals();
  const monthlyData = getMonthlyData();
  const categoryData = getCategoryBreakdown();

  const lineChartData = {
    labels: monthlyData.labels,
    datasets: [
      {
        label: 'Income',
        data: monthlyData.income,
        borderColor: '#059669',
        backgroundColor: 'rgba(5, 150, 105, 0.1)',
        tension: 0.4,
      },
      {
        label: 'Expenses',
        data: monthlyData.expenses,
        borderColor: '#dc2626',
        backgroundColor: 'rgba(220, 38, 38, 0.1)',
        tension: 0.4,
      }
    ]
  };

  const barChartData = {
    labels: monthlyData.labels,
    datasets: [
      {
        label: 'Net Savings',
        data: monthlyData.income.map((inc, idx) => inc - monthlyData.expenses[idx]),
        backgroundColor: monthlyData.income.map((inc, idx) => 
          inc - monthlyData.expenses[idx] >= 0 ? 'rgba(5, 150, 105, 0.8)' : 'rgba(220, 38, 38, 0.8)'
        ),
      }
    ]
  };

  const doughnutChartData = {
    labels: categoryData.labels,
    datasets: [
      {
        data: categoryData.data,
        backgroundColor: [
          '#667eea',
          '#764ba2',
          '#f093fb',
          '#4facfe',
          '#43e97b',
          '#fa709a',
        ],
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          font: {
            family: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
            size: 12
          }
        }
      },
      tooltip: {
        titleFont: {
          family: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif"
        },
        bodyFont: {
          family: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif"
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          font: {
            family: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
            size: 12
          }
        }
      },
      x: {
        ticks: {
          maxRotation: selectedMonth !== 'all' ? 90 : 0,
          minRotation: selectedMonth !== 'all' ? 45 : 0,
          autoSkip: selectedMonth !== 'all' ? false : true,
          font: {
            family: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
            size: selectedMonth !== 'all' ? 10 : 12,
          }
        },
        grid: {
          display: true,
        }
      }
    }
  };

  const barChartOptions = {
    ...chartOptions,
  };

  if (loading) {
    return (
      <div className="analytics-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="analytics-container">
      <div className="analytics-header">
        <div className="header-content">
          <h1>📊 Analytics</h1>
          <p className="viewing-text">
            Viewing: <strong>{getSelectedWalletsDisplay()}</strong> • <strong>{getPeriodLabel()}</strong>
          </p>
        </div>
      </div>

      {/* Filter Section */}
      <div className="filter-section">
        <div className="period-selector">
          <button className="nav-btn" onClick={goToPreviousMonth} title="Previous month">
            ◀
          </button>
          
          <div className="filter-controls">
            <select 
              className="month-select"
              value={selectedMonth} 
              onChange={(e) => setSelectedMonth(e.target.value)}
            >
              {months.map(month => (
                <option key={month.value} value={month.value}>
                  {month.label}
                </option>
              ))}
            </select>

            <select 
              className="year-select"
              value={selectedYear} 
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            >
              {getAvailableYears().map(year => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>

          <button className="nav-btn" onClick={goToNextMonth} title="Next month">
            ▶
          </button>
        </div>

        <button 
          className="reset-filter-btn"
          onClick={() => {
            setSelectedMonth('all');
            setSelectedYear(new Date().getFullYear());
          }}
        >
          🔄 Reset to Current Year
        </button>
      </div>

      {/* Summary Cards */}
      <div className="summary-grid">
        <div className="summary-card income-card">
          <div className="summary-header">
            <span className="summary-label">Total Income</span>
            <span className="summary-icon">💰</span>
          </div>
          <div className="summary-amount income-amount">{formatMoney(totals.income)}</div>
        </div>

        <div className="summary-card expense-card">
          <div className="summary-header">
            <span className="summary-label">Total Expenses</span>
            <span className="summary-icon">💸</span>
          </div>
          <div className="summary-amount expense-amount">{formatMoney(totals.expenses)}</div>
        </div>

        <div className="summary-card savings-card">
          <div className="summary-header">
            <span className="summary-label">Savings</span>
            <span className="summary-icon">🏦</span>
          </div>
          <div className="summary-amount savings-amount">{formatMoney(totals.savings)}</div>
        </div>

        <div className="summary-card remaining-card">
          <div className="summary-header">
            <span className="summary-label">Remaining</span>
            <span className="summary-icon">💵</span>
          </div>
          <div className={`summary-amount ${totals.remaining >= 0 ? 'net-positive' : 'net-negative'}`}>
            {totals.remaining >= 0 ? '+' : '-'}{formatMoney(totals.remaining)}
          </div>
        </div>
      </div>

      {/* Budget Tracker - Monthly or Yearly */}
      {selectedMonth === 'all' ? (
        <YearlyBudgetTracker
          selectedYear={selectedYear}
          selectedWallets={selectedWallets}
          refreshKey={budgetRefreshKey}
        />
      ) : (
        <BudgetTracker 
          selectedMonth={selectedMonth} 
          selectedYear={selectedYear} 
          months={months} 
          selectedWallets={selectedWallets}
          refreshKey={budgetRefreshKey}
        />
      )}

      {/* Charts */}
      <div className="charts-grid">
        <div className="chart-card large">
          <h3>{selectedMonth !== 'all' ? `Daily ${months.find(m => m.value === selectedMonth)?.label} ${selectedYear}` : `${selectedYear}`} - Income vs Expenses</h3>
          <div className="chart-wrapper">
            <Line data={lineChartData} options={chartOptions} />
          </div>
        </div>

        <div className="chart-card">
          <h3>{selectedMonth !== 'all' ? `Daily Net Savings - ${months.find(m => m.value === selectedMonth)?.label}` : 'Monthly Net Savings'}</h3>
          <div className="chart-wrapper">
            <Bar data={barChartData} options={barChartOptions} />
          </div>
        </div>

        <div className="chart-card">
          <h3>{selectedMonth !== 'all' ? `${months.find(m => m.value === selectedMonth)?.label}` : `${selectedYear}`} - Expenses by Category</h3>
          <div className="chart-wrapper">
            <Doughnut data={doughnutChartData} options={chartOptions} />
          </div>
        </div>

        {/* AI Analysis */}
        <AIAnalysis 
          income={getFilteredIncome()}
          expenses={getFilteredExpenses()}
          selectedMonth={selectedMonth}
          selectedYear={selectedYear}
          months={months}
	  onSettingsChange={() => setBudgetRefreshKey(prev => prev + 1)}
        />
      </div>
    </div>
  );
}

export default Analytics;
